import React, { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from '../auth/hooks/useAuth';
import { useAbortableFetch } from '../../hooks/useAbortableFetch';
import { submissionService } from '../../services/submissionService';
import { kpiService } from '../../services/kpiService';
import { mapSupabaseError } from '../../utils/supabaseErrors';
import { ConfirmDialog } from '../shared/ConfirmDialog';
import { SubmissionsTable } from './SubmissionsTable';
import { NewSubmissionChooser } from './NewSubmissionChooser';
import { KPISubmissionForm } from './KPISubmissionForm';
import { PISubmissionForm } from './PISubmissionForm';
import { ReportSubmissionForm } from './ReportSubmissionForm';
import { performanceService } from '../../services/performanceService';
import { reportService } from '../../services/reportService';
import './MySubmissions.css';

const DRAFT_STATES = ['draft', 'rejected'];

export function MySubmissionsPage() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [assignedSystems, setAssignedSystems] = useState([]);
  const [piSystems, setPiSystems] = useState([]);
  const [reportSystems, setReportSystems] = useState([]);
  const [tab, setTab] = useState('drafts');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [chooserOpen, setChooserOpen] = useState(false);
  const [formSubmission, setFormSubmission] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = async (ctx = {}) => {
    const isCancelled = ctx.isCancelled ?? (() => false);
    try {
      setLoading(true);
      const [subs, kpiAssignments, piAssignments, reportAssignments] = await Promise.all([
        submissionService.listMySubmissions(),
        kpiService.getAssignedSystems(user.id),
        performanceService.getAssignedPISystems(user.id),
        reportService.getAssignedReportSystems(user.id),
      ]);
      if (isCancelled()) return;
      setRows(subs);
      setAssignedSystems((kpiAssignments || []).map((a) => ({ id: a.kpi_system_id, name: a.kpi_systems?.name, enabled_periods: a.kpi_systems?.enabled_periods || [] })));
      setPiSystems((piAssignments || []).map((a) => ({ id: a.pi_system_id, name: a.pi_systems?.name, enabled_periods: a.pi_systems?.enabled_periods || [] })));
      setReportSystems((reportAssignments || []).map((a) => ({ id: a.report_system_id, name: a.report_systems?.name, enabled_periods: a.report_systems?.enabled_periods || [], per_country: !!a.report_systems?.per_country })));
    } catch (e) {
      if (!isCancelled()) setError(mapSupabaseError(e));
    } finally {
      if (!isCancelled()) setLoading(false);
    }
  };

  useAbortableFetch(({ isCancelled }) => { if (user) load({ isCancelled }); }, [user?.id]);
  const refresh = () => load();

  const drafts = useMemo(() => rows.filter((r) => DRAFT_STATES.includes(r.status)), [rows]);
  const submitted = useMemo(() => rows.filter((r) => !DRAFT_STATES.includes(r.status)), [rows]);

  const handleContinue = async ({ type, period, kpiSystemId, piSystemId, reportSystemId, periodStart, periodEnd, countryId }) => {
    setChooserOpen(false);
    try {
      const draft = await submissionService.createDraft({ userId: user.id, type, period, periodStart, periodEnd, kpiSystemId, piSystemId, reportSystemId, countryId });
      setFormSubmission({ ...draft, user_id: user.id });
    } catch (e) {
      try {
        const existing = (await submissionService.listMySubmissions()).find(
          (r) => r.type === type
            && (type === 'performance' ? r.pi_system_id === piSystemId
                : type === 'report' ? (r.report_system_id === reportSystemId && (r.country_id || null) === (countryId || null))
                : r.kpi_system_id === kpiSystemId)
            && r.period_start === periodStart && r.period_end === periodEnd
        );
        if (existing) {
          if (existing.status === 'draft' || existing.status === 'rejected') {
            setFormSubmission({ ...existing, user_id: user.id }); // resume the editable draft
          } else {
            toast.error(`You already have a ${existing.status} submission for this period.`);
          }
          return;
        }
      } catch { /* fall through */ }
      toast.error(mapSupabaseError(e));
    }
  };

  const closeForm = (didChange) => { setFormSubmission(null); if (didChange) refresh(); };

  const confirmDelete = async () => {
    const target = deleteTarget;
    setDeleteTarget(null);
    try { await submissionService.deleteDraft(target.id); toast.success('Draft deleted'); refresh(); }
    catch (e) { toast.error(mapSupabaseError(e)); }
  };

  if (loading) return <p className="loading-msg">Loading your submissions…</p>;

  return (
    <div className="my-submissions">
      <div className="my-submissions-header">
        <h2>My Submissions</h2>
        <button className="btn-edit" onClick={() => setChooserOpen(true)}>+ New Submission</button>
      </div>
      {error && <div className="error-message" role="alert">{error}</div>}

      <div className="submissions-tabs" role="tablist" aria-label="My submissions">
        <button role="tab" aria-selected={tab === 'drafts'} className={`tab ${tab === 'drafts' ? 'active' : ''}`} onClick={() => setTab('drafts')}>Drafts</button>
        <button role="tab" aria-selected={tab === 'submitted'} className={`tab ${tab === 'submitted' ? 'active' : ''}`} onClick={() => setTab('submitted')}>Submitted</button>
      </div>

      {tab === 'drafts' ? (
        <SubmissionsTable rows={drafts} emptyText="No drafts — click New Submission to start."
          onEdit={(r) => setFormSubmission({ ...r, user_id: user.id })}
          onSubmit={(r) => setFormSubmission({ ...r, user_id: user.id })}
          onDelete={(r) => setDeleteTarget(r)} />
      ) : (
        <SubmissionsTable rows={submitted} emptyText="Nothing submitted yet."
          onView={(r) => setFormSubmission({ ...r, user_id: user.id })} />
      )}

      {chooserOpen && (
        <NewSubmissionChooser assignedSystems={assignedSystems} piSystems={piSystems} reportSystems={reportSystems} onContinue={handleContinue} onClose={() => setChooserOpen(false)} />
      )}
      {formSubmission && (
        formSubmission.type === 'report'
          ? <ReportSubmissionForm submission={formSubmission}
              onSaved={() => closeForm(true)} onSubmitted={() => closeForm(true)} onClose={() => closeForm(false)} />
          : formSubmission.type === 'performance'
            ? <PISubmissionForm submission={formSubmission}
                onSaved={() => closeForm(true)} onSubmitted={() => closeForm(true)} onClose={() => closeForm(false)} />
            : <KPISubmissionForm submission={formSubmission}
                onSaved={() => closeForm(true)} onSubmitted={() => closeForm(true)} onClose={() => closeForm(false)} />
      )}
      <ConfirmDialog isOpen={!!deleteTarget} title="Delete draft?" message="This draft and its values will be permanently removed."
        confirmText="Delete" cancelText="Keep" onConfirm={confirmDelete} onCancel={() => setDeleteTarget(null)} />
    </div>
  );
}
